import {
  AuthorizeConnectionStep,
  CredentialsStep,
  DatabaseStep,
  SelectFileTypeStep,
  SelectSiteStep,
  FinalStep,
  ServerStep,
} from '@/ui/components/integrations/details/multiStepModal/steps';
import { ConfigPage, ConfigVariableKey, IntegrationDetailed, RequiredConfigVariable } from './types';
import { Component } from 'vue';

interface StepConfig {
  component: Component;
  configVariable?: RequiredConfigVariable;
}

class MultiStepConfigService {
  private readonly FILE_FORMAT_VARIABLE = 'File Format';
  private readonly SHAREPOINT_SITE_VARIABLE = 'Site';

  public formatSteps(integration: IntegrationDetailed, configVariables: RequiredConfigVariable[]): StepConfig[] {
    const steps = this
      .sortConfigVariables(integration, configVariables)
      .map(configVariable => this.formatStepsForVariable(configVariable))
      .flat();

    steps.push(this.formatFinalStep());

    return steps;
  }

  private formatStepsForVariable(configVariable: RequiredConfigVariable): StepConfig[] {
    if (configVariable.key === this.FILE_FORMAT_VARIABLE) {
      return [
        {
          component: SelectFileTypeStep,
          configVariable,
        },
      ];
    }

    if (configVariable.key === this.SHAREPOINT_SITE_VARIABLE) {
      return [
        {
          component: SelectSiteStep,
          configVariable,
        },
      ];
    }

    if (configVariable.dataType === 'CONNECTION') {
      if (configVariable.connection.oauth2Type === 'AUTHORIZATION_CODE') {
        return [
          {
            component: AuthorizeConnectionStep,
            configVariable,
          },
        ];
      }

      return [
        {
          component: DatabaseStep,
          configVariable,
        },
        {
          component: ServerStep,
          configVariable,
        },
        {
          component: CredentialsStep,
          configVariable,
        },
      ];
    }

    console.error('Unknown config variable type: ', { configVariable });
    throw new Error('Unknown config variable type', { cause: configVariable });
  }

  private formatFinalStep(): StepConfig {
    return {
      component: FinalStep,
    };
  }

  /**
   * Sort config variables according to the order of elements in configPages.
   */
  private sortConfigVariables(integration: IntegrationDetailed, configVariables: RequiredConfigVariable[]): RequiredConfigVariable[] {
    const pages = JSON.parse(integration.configPages) as ConfigPage[];
    const indexMap = {} as Record<ConfigVariableKey, number>;
    let index = 0;
    pages.forEach(page => {
      page.elements.forEach(element => {
        indexMap[element.value] = index++;
      });
    });

    return configVariables.sort((a, b) => {
      return indexMap[a.key] - indexMap[b.key];
    });
  }
}

export const multiStepConfigService = new MultiStepConfigService;
